import React, { useState, useMemo } from "react";
import styles from "./ListLayout.module.css";
import { Button, Dropdown } from "react-bootstrap";
import { FiSearch, FiX, FiFilter } from "react-icons/fi";
import { FaFileExport } from "react-icons/fa";

const ListLayout = ({
  title,
  searchValue,
  onSearch,
  onClearSearch,
  showSearch = true,
  exportOptions = [],
  onNew,
  children,
  pagination,
  FilterComponent,
  filterWidth = 350,
  filterBtnColor = "outline-secondary",
  activeFilterColor = "outline-info", // ✅ changed default (NOT primary)
  showFilterCount = true,
  onApplyFilter,
  onNewText = "+ New",
  onNewLoading = false,
}) => {
  const [showFilter, setShowFilter] = useState(false);
  const [filterValues, setFilterValues] = useState({});

  const handleFilterChange = (key, value) => {
    setFilterValues((prev) => ({ ...prev, [key]: value }));
  };

  // ✅ Clean + Apply filters
  const applyFilter = () => {
    const cleanFilters = Object.fromEntries(
      Object.entries(filterValues).filter(
        ([_, v]) => v !== "" && v !== false && v != null
      )
    );

    onApplyFilter && onApplyFilter(cleanFilters);
    setShowFilter(false);
  };

  const clearFilter = () => {
    setFilterValues({});
    onApplyFilter && onApplyFilter({});
  };

  // ✅ Active filters
  const activeFilters = useMemo(() => {
    return Object.entries(filterValues).filter(
      ([_, v]) => v !== "" && v !== false && v != null
    );
  }, [filterValues]);

  const hasFilterApplied = activeFilters.length > 0;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        {/* ✅ ROW 1 → TITLE + ACTIONS */}
        <div className={styles.headerTop}>
          <div className={styles.left}>
            <h5 title={title}>{title}</h5>
          </div>

          <div className={styles.actions}>
            {/* EXPORT */}
            {exportOptions.length > 0 && (
              <Dropdown align="end">
                <Dropdown.Toggle size="sm" variant="outline-secondary">
                  <FaFileExport className="me-1" />
                  <span className={styles.btnText}>Export</span>
                </Dropdown.Toggle>
                <Dropdown.Menu>
                  {exportOptions.map((item, i) => (
                    <Dropdown.Item key={i} onClick={item.onClick}>
                      {item.icon} <span className="ms-2">{item.label}</span>
                    </Dropdown.Item>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            )}

            {/* NEW */}
            {onNew && (
              <Button size="sm" onClick={onNew}>
                {onNewLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    {"Submitting..."}
                  </>
                ) : (
                  <>
                    {onNewText !== "" ? (
                      <span className={styles.btnText}>{onNewText}</span>
                    ) : (
                      +(<span className={styles.btnText}>New</span>)
                    )}
                  </>
                )}
              </Button>
            )}

            {/* FILTER */}
            {FilterComponent && (
              <Button
                size="sm"
                variant={hasFilterApplied ? activeFilterColor : filterBtnColor}
                className={`${styles.filterBtn} ${
                  hasFilterApplied ? styles.activeFilterBtn : ""
                }`}
                onClick={() => setShowFilter(true)}
              >
                <FiFilter
                  className={hasFilterApplied ? styles.activeIcon : ""}
                />
                <span className={styles.btnText}>
                  {hasFilterApplied
                    ? `Filtered (${activeFilters.length})`
                    : "Filter"}
                </span>

                {hasFilterApplied && showFilterCount && (
                  <span className={styles.filterDot}></span>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* ✅ ROW 2 → SEARCH + PAGINATION */}
        {(showSearch || pagination) && (
          <div className={styles.headerBottom}>
            {showSearch && (
              <div className={styles.searchBox}>
                <FiSearch />
                <input
                  value={searchValue}
                  onChange={(e) => onSearch(e.target.value)}
                  placeholder="Search..."
                />
                {searchValue && (
                  <FiX className={styles.clearIcon} onClick={onClearSearch} />
                )}
              </div>
            )}

            {pagination && (
              <div className={styles.paginationInline}>{pagination}</div>
            )}
          </div>
        )}
      </div>

      {/* CONTENT */}
      <div className={styles.content}>{children}</div>

      {/* FILTER SLIDER */}
      {FilterComponent && (
        <>
          <div
            className={`${styles.filterSlider} ${
              showFilter ? styles.showSlider : ""
            }`}
            style={{ width: filterWidth }}
          >
            <div className={styles.sliderHeader}>
              <h6>Filter</h6>
              <FiX
                className={styles.closeSlider}
                onClick={() => setShowFilter(false)}
              />
            </div>

            <div className={styles.sliderContent}>
              {/* Applied filters */}
              {hasFilterApplied && (
                <div className={styles.appliedFilters}>
                  {activeFilters.map(([key, value]) => (
                    <span key={key}>
                      {key}: {value.toString()}
                    </span>
                  ))}

                  <Button size="sm" variant="link" onClick={clearFilter}>
                    Clear All
                  </Button>
                </div>
              )}

              <FilterComponent
                {...filterValues}
                onChange={handleFilterChange}
              />
            </div>

            <div className={styles.sliderFooter}>
              <Button onClick={applyFilter} className="w-100">
                Apply
              </Button>
            </div>
          </div>

          {/* OVERLAY */}
          {showFilter && (
            <div
              className={styles.overlay}
              onClick={() => setShowFilter(false)}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ListLayout;
